import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Tag,
  message,
} from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { format } from "date-fns";
import {
  createStockIssue,
  getStockIssueById,
  getStockIssues,
  updateStockIssueStatus,
} from "../../apis/stockIssues";
import { getDepartments } from "../../apis/departments";
import { getUsers } from "../../apis/user";
import { getPharmacyItems, getPharmacyItemBatches } from "../../apis/pharmacyItems";
import { useNotification } from "../../NotificationContext";
import CommonPagination from "../../components/CommanPagination";

const { Option } = Select;
const { TextArea } = Input;

const toArray = (res: any): any[] => {
  if (Array.isArray(res)) return res;
  if (Array.isArray(res?.data)) return res.data;
  if (Array.isArray(res?.data?.data)) return res.data.data;
  return [];
};

interface IssueLine {
  key: string;
  pharmacyItemId?: number;
  batchId?: number;
  quantity: number;
  unitCost?: number;
  batches: any[];
  loadingBatches?: boolean;
}

const InternalIssuesList: React.FC = () => {
  const { showNotification } = useNotification();
  const [rows, setRows] = useState<any[]>([]);
  const [meta, setMeta] = useState<any>();
  const [loading, setLoading] = useState(false);

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [departments, setDepartments] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [pharmacyItems, setPharmacyItems] = useState<any[]>([]);
  const [itemSearchResults, setItemSearchResults] = useState<any[]>([]);
  const [itemSearchLoading, setItemSearchLoading] = useState(false);
  const itemSearchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [status, setStatus] = useState<"requested" | "issued">("requested");
  const [departmentId, setDepartmentId] = useState<number | null>(null);
  const [requestedByUserId, setRequestedByUserId] = useState<number | null>(null);
  const [notes, setNotes] = useState("");
  const [lines, setLines] = useState<IssueLine[]>([
    { key: `line-${Date.now()}`, quantity: 1, batches: [] },
  ]);

  const [selected, setSelected] = useState<any | null>(null);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [issuingId, setIssuingId] = useState<number | null>(null);

  useEffect(() => {
    fetchRows({ page: 1, per_page: 10 });
    return () => {
      if (itemSearchTimerRef.current) clearTimeout(itemSearchTimerRef.current);
    };
  }, []);

  const fetchRows = (params: Record<string, any> = {}) => {
    setLoading(true);
    getStockIssues({ per_page: 10, ...params })
      .then((res: any) => {
        const data = res.data?.data || res.data || [];
        const metaData = res.meta || res.data?.meta || {};
        setRows(Array.isArray(data) ? data : []);
        setMeta(metaData);
      })
      .catch(() => showNotification("error", "Failed to fetch internal issues", "", "topRight"))
      .finally(() => setLoading(false));
  };

  const mergePharmacyItems = (items: any[]) => {
    setPharmacyItems((prev) => {
      const existingIds = new Set(prev.map((p) => p.id));
      const newItems = items.filter((r) => !existingIds.has(r.id));
      return newItems.length ? [...prev, ...newItems] : prev;
    });
  };

  const openCreateModal = async () => {
    setIsCreateOpen(true);
    setStatus("requested");
    setDepartmentId(null);
    setRequestedByUserId(null);
    setNotes("");
    setLines([{ key: `line-${Date.now()}`, quantity: 1, batches: [] }]);
    setItemSearchResults([]);

    try {
      const [deptRes, userRes, itemRes]: any[] = await Promise.all([
        getDepartments({ per_page: 200 }),
        getUsers({ per_page: 200 }),
        getPharmacyItems({ per_page: 50, is_active: true }),
      ]);
      const items = toArray(itemRes);
      setDepartments(toArray(deptRes));
      setUsers(toArray(userRes));
      setPharmacyItems(items);
      setItemSearchResults(items);
    } catch {
      message.error("Failed to load form options");
    }
  };

  const handleItemSearch = (value: string) => {
    if (itemSearchTimerRef.current) clearTimeout(itemSearchTimerRef.current);
    const q = value.trim();
    if (!q) {
      setItemSearchResults(pharmacyItems.slice(0, 50));
      return;
    }
    itemSearchTimerRef.current = setTimeout(async () => {
      setItemSearchLoading(true);
      try {
        const response: any = await getPharmacyItems({
          search: q,
          per_page: 50,
          is_active: true,
        });
        const results = toArray(response);
        setItemSearchResults(results);
        mergePharmacyItems(results);
      } catch {
        message.error("Failed to search items");
      } finally {
        setItemSearchLoading(false);
      }
    }, 300);
  };

  const handleItemChange = async (key: string, pharmacyItemId: number) => {
    const selectedItem =
      itemSearchResults.find((i) => Number(i.id) === Number(pharmacyItemId)) ||
      pharmacyItems.find((i) => Number(i.id) === Number(pharmacyItemId));
    if (selectedItem) mergePharmacyItems([selectedItem]);

    setLines((prev) =>
      prev.map((line) =>
        line.key === key
          ? {
              ...line,
              pharmacyItemId,
              batchId: undefined,
              unitCost: undefined,
              batches: [],
              loadingBatches: true,
            }
          : line
      )
    );

    try {
      const res: any = await getPharmacyItemBatches(pharmacyItemId);
      const batches = res?.batches || res?.data?.batches || res?.data || [];
      setLines((prev) =>
        prev.map((line) =>
          line.key === key
            ? {
                ...line,
                batches: Array.isArray(batches) ? batches : [],
                loadingBatches: false,
              }
            : line
        )
      );
    } catch {
      message.error("Failed to load batches");
      setLines((prev) =>
        prev.map((line) =>
          line.key === key ? { ...line, loadingBatches: false, batches: [] } : line
        )
      );
    }
  };

  const itemOptions = useMemo(() => {
    const byId = new Map<number, any>();
    itemSearchResults.forEach((item) => byId.set(Number(item.id), item));
    lines.forEach((line) => {
      if (!line.pharmacyItemId || byId.has(Number(line.pharmacyItemId))) return;
      const cached = pharmacyItems.find(
        (p) => Number(p.id) === Number(line.pharmacyItemId)
      );
      if (cached) byId.set(Number(cached.id), cached);
    });
    return Array.from(byId.values()).map((item) => ({
      value: item.id,
      label: `${item.name}${item.sku ? ` (${item.sku})` : ""}`,
    }));
  }, [itemSearchResults, pharmacyItems, lines]);

  const handleBatchChange = (key: string, batchId: number) => {
    setLines((prev) =>
      prev.map((line) => {
        if (line.key !== key) return line;
        const batch = line.batches.find((b) => Number(b.id) === Number(batchId));
        return {
          ...line,
          batchId,
          unitCost: Number(batch?.purchasePrice ?? batch?.purchase_price ?? 0),
        };
      })
    );
  };

  const addLine = () => {
    setLines((prev) => [...prev, { key: `line-${Date.now()}`, quantity: 1, batches: [] }]);
  };

  const removeLine = (key: string) => {
    setLines((prev) => (prev.length <= 1 ? prev : prev.filter((line) => line.key !== key)));
  };

  const totalCost = useMemo(
    () =>
      lines.reduce(
        (sum, line) => sum + (Number(line.unitCost) || 0) * (Number(line.quantity) || 0),
        0
      ),
    [lines]
  );

  const handleCreate = async () => {
    if (!departmentId) {
      message.warning("Please select a department");
      return;
    }
    const validLines = lines.filter(
      (line) => line.pharmacyItemId && line.batchId && line.quantity > 0
    );
    if (validLines.length === 0) {
      message.warning("Add at least one pharmacy item with batch and quantity");
      return;
    }

    setCreating(true);
    try {
      await createStockIssue({
        department_id: departmentId,
        requested_by_user_id: requestedByUserId || undefined,
        status,
        notes: notes || null,
        items: validLines.map((line) => ({
          pharmacy_item_id: line.pharmacyItemId!,
          batch_id: line.batchId!,
          quantity: line.quantity,
        })),
      });
      message.success(
        status === "issued"
          ? "Stock issued and inventory updated"
          : "Stock issue request created"
      );
      setIsCreateOpen(false);
      fetchRows({ page: meta?.current_page || 1, per_page: meta?.per_page || 10 });
    } catch (err: any) {
      const details =
        err?.response?.data?.details?.message ||
        err?.response?.data?.message ||
        err?.message ||
        "Failed to create stock issue";
      message.error(details);
    } finally {
      setCreating(false);
    }
  };

  const handleView = (id: number) => {
    getStockIssueById(id)
      .then((res: any) => {
        setSelected(res.data?.data || res.data || res);
        setIsViewOpen(true);
      })
      .catch(() => message.error("Failed to fetch issue details"));
  };

  const handleMarkIssued = async (id: number) => {
    setIssuingId(id);
    try {
      await updateStockIssueStatus(id, { status: "issued" });
      message.success("Marked as issued. Stock moved under internal.");
      fetchRows({ page: meta?.current_page || 1, per_page: meta?.per_page || 10 });
      if (selected?.id === id) {
        const res: any = await getStockIssueById(id);
        setSelected(res.data?.data || res.data || res);
      }
    } catch (err: any) {
      const details =
        err?.response?.data?.details?.message ||
        err?.response?.data?.message ||
        "Failed to mark as issued";
      message.error(details);
    } finally {
      setIssuingId(null);
    }
  };

  const onPaginationChange = (page: number, pageSize?: number) => {
    fetchRows({ page, per_page: pageSize || 10 });
  };

  const formatCurrency = (amount: number | string | undefined) =>
    `₹${(Number(amount) || 0).toFixed(2)}`;

  const userLabel = (user: any) =>
    `${user?.firstName || user?.first_name || ""} ${user?.lastName || user?.last_name || ""}`.trim() ||
    user?.email ||
    `#${user?.id}`;

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-base font-semibold leading-6 text-gray-900">Internal Issues</h1>
          <p className="mt-1 text-sm text-gray-500">
            Issue pharmacy stock to internal departments at purchase cost. Stock moves only when
            status is Issued.
          </p>
        </div>
        <button
          type="button"
          onClick={openCreateModal}
          className="mt-4 sm:mt-0 px-3 py-2 text-sm font-semibold text-white rounded-md shadow-sm bg-primary hover:bg-hover"
        >
          Create Stock Issue
        </button>
      </div>

      <div className="flow-root mt-8">
        <div className="overflow-x-auto -mx-4 -my-2 sm:-mx-6 lg:-mx-8">
          <div className="inline-block py-2 min-w-full align-middle sm:px-6 lg:px-8">
            <div className="overflow-hidden ring-1 ring-black ring-opacity-5 shadow sm:rounded-lg">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                      Issue
                    </th>
                    <th className="py-3.5 px-3 text-left text-sm font-semibold text-gray-900">
                      Department
                    </th>
                    <th className="py-3.5 px-3 text-left text-sm font-semibold text-gray-900">
                      Requested By
                    </th>
                    <th className="py-3.5 px-3 text-left text-sm font-semibold text-gray-900">
                      Items
                    </th>
                    <th className="py-3.5 px-3 text-left text-sm font-semibold text-gray-900">
                      Cost
                    </th>
                    <th className="py-3.5 px-3 text-left text-sm font-semibold text-gray-900">
                      Status
                    </th>
                    <th className="py-3.5 px-3 text-left text-sm font-semibold text-gray-900">
                      Issue Date
                    </th>
                    <th className="py-3.5 px-3 text-left text-sm font-semibold text-gray-900">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {loading ? (
                    <tr>
                      <td colSpan={8} className="py-8 text-center text-gray-500">
                        Loading...
                      </td>
                    </tr>
                  ) : rows.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="py-8 text-center text-gray-500">
                        No internal issues found
                      </td>
                    </tr>
                  ) : (
                    rows.map((row) => (
                      <tr key={row.id} className="hover:bg-gray-50">
                        <td className="py-4 pr-3 pl-4 text-sm sm:pl-6">
                          <div className="font-medium text-gray-900">
                            {row.issueNumber || `#${row.id}`}
                          </div>
                        </td>
                        <td className="px-3 py-4 text-sm text-gray-700">
                          {row.department?.name || "-"}
                        </td>
                        <td className="px-3 py-4 text-sm text-gray-700">
                          {row.requestedBy
                            ? `${row.requestedBy.firstName || ""} ${row.requestedBy.lastName || ""}`.trim()
                            : "-"}
                        </td>
                        <td className="px-3 py-4 text-sm text-gray-700">
                          {row.itemsCount ?? 0} / qty {row.totalQuantity ?? 0}
                        </td>
                        <td className="px-3 py-4 text-sm font-semibold text-gray-900">
                          {formatCurrency(row.totalCost)}
                        </td>
                        <td className="px-3 py-4 text-sm">
                          <Tag color={row.status === "issued" ? "green" : "orange"}>
                            {(row.status || "").toUpperCase()}
                          </Tag>
                        </td>
                        <td className="px-3 py-4 text-sm text-gray-700">
                          {row.issueDate
                            ? format(new Date(row.issueDate), "dd MMM yyyy, h:mm a")
                            : "-"}
                        </td>
                        <td className="px-3 py-4 text-sm">
                          <div className="flex gap-2">
                            <Button size="small" onClick={() => handleView(row.id)}>
                              View
                            </Button>
                            {row.status === "requested" ? (
                              <Button
                                size="small"
                                type="primary"
                                loading={issuingId === row.id}
                                onClick={() => handleMarkIssued(row.id)}
                              >
                                Mark Issued
                              </Button>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <CommonPagination meta={meta} onChange={onPaginationChange} />
          </div>
        </div>
      </div>

      <Modal
        title="Create Stock Issue"
        open={isCreateOpen}
        onCancel={() => setIsCreateOpen(false)}
        width={900}
        footer={[
          <Button key="cancel" onClick={() => setIsCreateOpen(false)}>
            Cancel
          </Button>,
          <Button key="save" type="primary" loading={creating} onClick={handleCreate}>
            {status === "issued" ? "Create & Issue Stock" : "Create Request"}
          </Button>,
        ]}
      >
        <Form layout="vertical" className="mt-2">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Form.Item label="Department" required>
              <Select
                showSearch
                placeholder="Select department"
                value={departmentId ?? undefined}
                onChange={(value) => setDepartmentId(value)}
                optionFilterProp="label"
                options={departments.map((dept) => ({
                  value: dept.id,
                  label: dept.name,
                }))}
                filterOption={(input, option) =>
                  String(option?.label ?? "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
              />
            </Form.Item>

            <Form.Item label="Requested By">
              <Select
                showSearch
                allowClear
                placeholder="Select user"
                value={requestedByUserId ?? undefined}
                onChange={(value) => setRequestedByUserId(value ?? null)}
                optionFilterProp="label"
                options={users.map((user) => ({
                  value: user.id,
                  label: userLabel(user),
                }))}
                filterOption={(input, option) =>
                  String(option?.label ?? "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
              />
            </Form.Item>

            <Form.Item label="Status" required>
              <Select value={status} onChange={(value) => setStatus(value)}>
                <Option value="requested">Requested</Option>
                <Option value="issued">Issued</Option>
              </Select>
            </Form.Item>
          </div>

          <Form.Item label="Notes">
            <TextArea
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional notes"
            />
          </Form.Item>

          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-gray-800">Pharmacy Items</h3>
            <Button icon={<PlusOutlined />} size="small" onClick={addLine}>
              Add Item
            </Button>
          </div>

          <div className="space-y-3 max-h-80 overflow-auto pr-1">
            {lines.map((line) => (
              <div
                key={line.key}
                className="grid grid-cols-1 md:grid-cols-12 gap-2 items-end border rounded-md p-3 bg-gray-50"
              >
                <div className="md:col-span-4">
                  <label className="text-xs text-gray-600">Item</label>
                  <Select
                    showSearch
                    className="w-full"
                    placeholder="Type to search item..."
                    value={line.pharmacyItemId}
                    onChange={(value) => handleItemChange(line.key, value)}
                    onSearch={handleItemSearch}
                    filterOption={false}
                    loading={itemSearchLoading}
                    notFoundContent={
                      itemSearchLoading ? "Searching..." : "No items found"
                    }
                    options={itemOptions}
                  />
                </div>
                <div className="md:col-span-3">
                  <label className="text-xs text-gray-600">Batch</label>
                  <Select
                    className="w-full"
                    placeholder={line.loadingBatches ? "Loading..." : "Select batch"}
                    value={line.batchId}
                    loading={line.loadingBatches}
                    disabled={!line.pharmacyItemId || line.loadingBatches}
                    onChange={(value) => handleBatchChange(line.key, value)}
                  >
                    {line.batches.map((batch) => (
                      <Option key={batch.id} value={batch.id}>
                        {batch.batchNumber || batch.batch_number} · ₹
                        {Number(batch.purchasePrice ?? batch.purchase_price ?? 0).toFixed(2)}
                        {` · qty ${
                          batch.currentQuantity ??
                          batch.current_quantity ??
                          batch.availableQuantity ??
                          batch.available_quantity ??
                          "-"
                        }`}
                      </Option>
                    ))}
                  </Select>
                </div>
                <div className="md:col-span-2">
                  <label className="text-xs text-gray-600">Qty</label>
                  <InputNumber
                    className="w-full"
                    min={1}
                    value={line.quantity}
                    onChange={(value) =>
                      setLines((prev) =>
                        prev.map((l) =>
                          l.key === line.key ? { ...l, quantity: Number(value) || 1 } : l
                        )
                      )
                    }
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="text-xs text-gray-600">Unit Cost</label>
                  <Input
                    value={line.unitCost != null ? formatCurrency(line.unitCost) : "-"}
                    disabled
                  />
                </div>
                <div className="md:col-span-1 flex justify-end">
                  <Button
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => removeLine(line.key)}
                    disabled={lines.length === 1}
                  />
                </div>
              </div>
            ))}
          </div>

          <div className="mt-3 text-right text-sm font-semibold">
            Estimated Cost: {formatCurrency(totalCost)}
          </div>
          {status === "issued" ? (
            <p className="mt-2 text-xs text-amber-600">
              Status is Issued — stock will be deducted now with movement type internal, and issue
              date will be saved.
            </p>
          ) : (
            <p className="mt-2 text-xs text-gray-500">
              Status is Requested — no stock movement yet. Issue date will be saved when marked
              Issued.
            </p>
          )}
        </Form>
      </Modal>

      <Modal
        title="Internal Issue Details"
        open={isViewOpen}
        onCancel={() => {
          setIsViewOpen(false);
          setSelected(null);
        }}
        footer={[
          <Button
            key="close"
            onClick={() => {
              setIsViewOpen(false);
              setSelected(null);
            }}
          >
            Close
          </Button>,
          selected?.status === "requested" ? (
            <Button
              key="issue"
              type="primary"
              loading={issuingId === selected?.id}
              onClick={() => handleMarkIssued(selected.id)}
            >
              Mark Issued
            </Button>
          ) : null,
        ]}
        width={800}
      >
        {selected && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-semibold text-gray-700">Issue Number</p>
                <p className="text-sm text-gray-900">{selected.issueNumber || `-`}</p>
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-700">Status</p>
                <Tag color={selected.status === "issued" ? "green" : "orange"}>
                  {(selected.status || "").toUpperCase()}
                </Tag>
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-700">Department</p>
                <p className="text-sm text-gray-900">{selected.department?.name || "-"}</p>
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-700">Issue Date</p>
                <p className="text-sm text-gray-900">
                  {selected.issueDate
                    ? format(new Date(selected.issueDate), "dd MMM yyyy, h:mm a")
                    : "-"}
                </p>
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-700">Requested By</p>
                <p className="text-sm text-gray-900">
                  {selected.requestedBy
                    ? `${selected.requestedBy.firstName || ""} ${selected.requestedBy.lastName || ""}`.trim()
                    : "-"}
                </p>
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-700">Issued By</p>
                <p className="text-sm text-gray-900">
                  {selected.issuedBy
                    ? `${selected.issuedBy.firstName || ""} ${selected.issuedBy.lastName || ""}`.trim()
                    : "-"}
                </p>
              </div>
            </div>

            {selected.notes ? (
              <div>
                <p className="text-sm font-semibold text-gray-700">Notes</p>
                <p className="text-sm text-gray-900">{selected.notes}</p>
              </div>
            ) : null}

            <div>
              <p className="mb-2 text-sm font-semibold text-gray-700">Items</p>
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left">Item</th>
                    <th className="px-3 py-2 text-left">Batch</th>
                    <th className="px-3 py-2 text-right">Qty</th>
                    <th className="px-3 py-2 text-right">Unit Cost</th>
                    <th className="px-3 py-2 text-right">Line Cost</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {(selected.items || []).map((item: any) => (
                    <tr key={item.id}>
                      <td className="px-3 py-2">
                        {item.pharmacyItem?.name || "-"}
                        <div className="text-xs text-gray-400">{item.pharmacyItem?.sku}</div>
                      </td>
                      <td className="px-3 py-2">{item.batch?.batchNumber || "-"}</td>
                      <td className="px-3 py-2 text-right">{item.quantity}</td>
                      <td className="px-3 py-2 text-right">{formatCurrency(item.unitCost)}</td>
                      <td className="px-3 py-2 text-right">{formatCurrency(item.lineCost)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="mt-2 text-right font-semibold">
                Total: {formatCurrency(selected.totalCost)}
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default InternalIssuesList;
